import { CheckboxInput } from '@medeloop/medeloop-components-library';
import { useState, useEffect, useRef, useMemo } from 'react';
import { MaterialSymbol } from 'react-material-symbols';

export interface MultiSelectOption {
  label: string;
  value: string;
}

export interface MultiSelectProps {
  options: MultiSelectOption[];
  selectedValues?: string[];
  onChange?: (values: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
}

export const MultiSelect = ({
  options,
  selectedValues,
  onChange,
  placeholder = 'Select…',
  disabled = false,
}: MultiSelectProps) => {
  const [internal, setInternal] = useState<string[]>(selectedValues ?? []);
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const selected = selectedValues ?? internal;

  const labelMap = useMemo(
    () =>
      options.reduce<Record<string, string>>((acc, { value, label }) => {
        acc[value] = label;
        return acc;
      }, {}),
    [options],
  );

  useEffect(() => {
    if (selectedValues) {
      setInternal(selectedValues);
    }
  }, [selectedValues]);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const toggleValue = (value: string) => {
    const next = selected.includes(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value];
    setInternal(next);
    onChange?.(next);
  };

  return (
    <div className="w-full relative" ref={ref}>
      <button
        type="button"
        className="relative w-full min-h-[2.5rem] px-3 py-1 pr-8 flex flex-wrap items-center gap-1 border border-stroke-on-dark-subdued bg-background-dark-subdued rounded focus:outline-none disabled:opacity-50"
        onClick={() => !disabled && setIsOpen((o) => !o)}
        aria-expanded={isOpen}
        disabled={disabled}
      >
        {selected.length === 0 && (
          <span className="text-sm text-purple-gray-400">{placeholder}</span>
        )}

        <div className="flex flex-wrap items-center gap-1">
          {selected.map((v, index) => (
            <div
              key={`${v}-${index}`}
              className="inline-flex items-center gap-1 border border-stroke-on-dark-default text-content-on-dark-default rounded-full px-2 py-0.5 text-sm"
            >
              {/* now shows the mapped label or falls back to the raw value */}
              {labelMap[v] ?? v}
              <div
                role="button"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleValue(v);
                }}
                className="leading-none"
              >
                <MaterialSymbol icon="close" size={16} className="text-content-on-dark-default" />
              </div>
            </div>
          ))}
        </div>

        <MaterialSymbol
          icon="chevron_right"
          size={24}
          className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${
            isOpen ? '-rotate-90' : 'rotate-90'
          } text-content-on-dark-default`}
        />
      </button>

      {isOpen && (
        <ul className="absolute z-10 mt-1 w-3/4 max-h-60 overflow-auto bg-background-dark-subdued border border-stroke-on-dark-subdued rounded shadow-lg">
          {options.map((opt) => (
            <li key={opt.value} className="px-3 py-2 hover:bg-background-dark-subdued2">
              <CheckboxInput
                checked={selected.includes(opt.value)}
                label={opt.label}
                onChange={() => toggleValue(opt.value)}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
